import { PROMO_RATE_MODES } from '../constants/bookingConst';
import { formatMoney } from './currencyUtil';

// Promos are limited-time pricing (Admin -> Promo). A promo owns a date
// range and a sport; inside it, RULES narrow the discount down to particular
// courts, weekdays and hours. Everything is optional except the price
// itself, and an empty list always means "all of them" — a promo with no
// rule filters at all is simply "this price, this sport, these dates".
//
// Stored on `config.promos`, alongside packages and rate bands, so a promo
// travels with the rest of the club's pricing and needs no second Firebase
// node or migration.
//
// This file deliberately imports NOTHING from bookingUtils: bookingUtils
// calls into here from getCourtRate, and a cycle between the two would leave
// whichever module loaded second holding undefined helpers.

/** @returns {object[]} the configured promos, always an array. */
export const getPromos = (config) => (Array.isArray(config?.promos) ? config.promos : []);

/**
 * 0-6 (0 = Sunday) for a 'yyyy-MM-dd' string, or null.
 *
 * Parsed field by field rather than with `new Date(str)`, which reads a bare
 * date as UTC midnight and lands on the previous day west of Greenwich.
 *
 * @param {string} dateStr
 * @returns {number|null}
 */
const weekdayOf = (dateStr) => {
  const [y, m, d] = String(dateStr || '').split('-').map(Number);
  if (!y || !m || !d) return null;
  return new Date(y, m - 1, d).getDay();
};

/**
 * Whether `dateStr` falls inside a promo's run.
 *
 * Both ends are INCLUSIVE, and either may be blank: a promo with no end date
 * runs until the admin ends it, which is how an open-ended "opening month"
 * offer is expressed. Dates are 'yyyy-MM-dd', so string comparison is
 * chronological and no parsing is needed.
 *
 * @param {object} promo
 * @param {string} dateStr - 'yyyy-MM-dd'
 * @returns {boolean}
 */
export const isPromoActiveOn = (promo, dateStr) => {
  if (!promo || promo.enabled === false || !dateStr) return false;
  const start = String(promo.startDate || '');
  const end = String(promo.endDate || '');
  if (start && dateStr < start) return false;
  if (end && dateStr > end) return false;
  return true;
};

/**
 * Whether one rule covers a specific court-hour.
 *
 * An empty (or missing) list on any dimension means "every one of them",
 * matching how a rate band's `days` already works. That is what keeps the
 * common case — a promo across the whole sport — a matter of filling in one
 * price and nothing else.
 *
 * @param {object} rule
 * @param {{court: number|string, dateStr: string, timeSlot: string}} target
 * @returns {boolean}
 */
export const promoRuleMatches = (rule, { court, dateStr, timeSlot }) => {
  if (!rule) return false;

  const courts = (Array.isArray(rule.courts) ? rule.courts : []).map(Number).filter(Number.isFinite);
  if (courts.length > 0 && !courts.includes(Number(court))) return false;

  const days = (Array.isArray(rule.days) ? rule.days : []).map(Number).filter(Number.isFinite);
  if (days.length > 0) {
    const weekday = weekdayOf(dateStr);
    if (weekday === null || !days.includes(weekday)) return false;
  }

  const slots = Array.isArray(rule.timeSlots) ? rule.timeSlots : [];
  if (slots.length > 0 && !slots.includes(timeSlot)) return false;

  return true;
};

/**
 * What one rule charges, given the price that would otherwise apply.
 *
 * Rounded to whole pesos and floored at 0: a percentage off an odd rate
 * produces fractions the rest of the app has no way to display, and a
 * discount larger than the rate must not turn into money owed to the guest.
 *
 * @param {object} rule - { mode, value }
 * @param {number} baseRate - the normal rate-band price for this hour
 * @returns {number|null} the promo price, or null when the rule is unusable
 */
export const applyPromoRule = (rule, baseRate) => {
  const value = Number(rule?.value);
  if (!Number.isFinite(value)) return null;
  const base = Number(baseRate) || 0;

  switch (rule?.mode) {
    case PROMO_RATE_MODES.FIXED:
      return Math.max(0, Math.round(value));
    case PROMO_RATE_MODES.PERCENT:
      return Math.max(0, Math.round(base * (1 - (value / 100))));
    case PROMO_RATE_MODES.AMOUNT:
      return Math.max(0, Math.round(base - value));
    default:
      return null;
  }
};

/**
 * The best promo price for one court-hour, or null when none applies.
 *
 * BEST means cheapest. Two promos can legitimately overlap — a weekend offer
 * and a launch-month one — and picking the lowest is the only rule that is
 * both deterministic and defensible to a guest who was shown a price. Order
 * in the config would be neither.
 *
 * @param {object} config
 * @param {object} target
 * @param {number|string} target.court
 * @param {string} target.timeSlot
 * @param {string|null} target.sportId
 * @param {string|null} target.dateStr - 'yyyy-MM-dd'. Without one no promo
 *   can apply: there is no way to tell whether its run covers this booking.
 * @param {number} target.baseRate - the rate that applies without any promo
 * @returns {{price: number, promo: object, rule: object}|null}
 */
export const findPromoRate = (config, { court, timeSlot, sportId, dateStr, baseRate }) => {
  if (!dateStr) return null;

  let best = null;
  getPromos(config).forEach((promo) => {
    if (!isPromoActiveOn(promo, dateStr)) return;
    // A promo with no sport applies to every sport — the club-wide sale.
    if (promo.sportId && sportId && promo.sportId !== sportId) return;

    (Array.isArray(promo.rules) ? promo.rules : []).forEach((rule) => {
      if (!promoRuleMatches(rule, { court, dateStr, timeSlot })) return;
      const price = applyPromoRule(rule, baseRate);
      if (price === null) return;
      if (!best || price < best.price) best = { price, promo, rule };
    });
  });

  return best;
};

/**
 * The promo price for one court-hour, or the base rate when none applies.
 *
 * The one-line form getCourtRate calls, and the top of the app's pricing
 * order: promo, then the court's own override, then the sport's default
 * band. A matching promo wins outright — including one that works out
 * DEARER than the normal rate, because the admin typed that number against
 * these hours on purpose and a price the app silently declined to apply
 * would be worse than a surprising one. (Overlapping promos still resolve
 * to the cheapest; see findPromoRate.)
 *
 * The court override is not skipped by that: percentage and amount rules are
 * worked out FROM the base this is handed, so "20% off" on a court priced
 * above its sport discounts the court's own rate.
 *
 * @param {object} config
 * @param {object} target - as findPromoRate
 * @returns {number}
 */
export const applyPromoToRate = (config, target) => {
  const found = findPromoRate(config, target);
  return found ? found.price : (Number(target?.baseRate) || 0);
};

/**
 * Whether ANY promo covers one court-hour, without pricing it.
 *
 * For the grid, which marks promo cells before it knows or cares what they
 * cost. `baseRate` is irrelevant to matching — only to the number a match
 * produces — so it is passed as 0 rather than made the caller's problem.
 *
 * @param {object} config
 * @param {{court: number|string, timeSlot: string, sportId: string|null, dateStr: string|null}} target
 * @returns {boolean}
 */
export const hasActivePromo = (config, { court, timeSlot, sportId, dateStr }) => (
  !!findPromoRate(config, { court, timeSlot, sportId, dateStr, baseRate: 0 })
);

/**
 * "₱250/hr", "20% off" or "₱50 off" — how a rule reads on a card.
 * @param {object} rule
 * @returns {string}
 */
export const describePromoRate = (rule, config = null) => {
  const value = Number(rule?.value);
  if (!Number.isFinite(value)) return '—';
  switch (rule?.mode) {
    case PROMO_RATE_MODES.FIXED: return `${formatMoney(value, config)}/hr`;
    case PROMO_RATE_MODES.PERCENT: return `${value}% off`;
    case PROMO_RATE_MODES.AMOUNT: return `${formatMoney(value, config)} off`;
    default: return '—';
  }
};

/**
 * "Jan 5 – Feb 2, 2026", "From Jan 5, 2026", or "Any date".
 * @param {object} promo
 * @returns {string}
 */
export const describePromoDates = (promo) => {
  const fmt = (value) => {
    const [y, m, d] = String(value || '').split('-').map(Number);
    if (!y || !m || !d) return null;
    return new Date(y, m - 1, d).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  };
  const start = fmt(promo?.startDate);
  const end = fmt(promo?.endDate);
  if (start && end) return `${start} – ${end}`;
  if (start) return `From ${start}`;
  if (end) return `Until ${end}`;
  return 'Any date';
};

/**
 * Where a promo sits relative to today: what the card's status pill says.
 *
 * @param {object} promo
 * @param {string} todayStr - 'yyyy-MM-dd'
 * @returns {'disabled'|'scheduled'|'ended'|'running'}
 */
export const getPromoState = (promo, todayStr) => {
  if (promo?.enabled === false) return 'disabled';
  const start = String(promo?.startDate || '');
  const end = String(promo?.endDate || '');
  if (start && todayStr < start) return 'scheduled';
  if (end && todayStr > end) return 'ended';
  return 'running';
};
